import { useState } from "react";
import { Link } from "react-router-dom";
import {
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Chip,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Button,
    Tooltip,
} from "@heroui/react";
import { LuEye, LuPencil, LuTrash, LuPlus, LuRefreshCw } from "react-icons/lu";
import { useDataTable, useFetchData } from "@/hooks";
import {
    ActiveFilterChips,
    DataTableControlBar,
    DataTableSkeleton,
    DataTablePagination,
    DeleteConfirmModal,
    FilterButton,
    ChangeStatusModal,
} from "@/components/data-table";
import {
    EmptyState,
    ErrorState,
    ThemedText,
    PageHeader,
} from "@/components/ui";
import { formatCurrency, formatDate } from "@/utils";
import { FilterData } from "./components";
import { useUser } from "@/context/UserContext";

// Initial filter values
const initialFilters = {
    return_number: "",
    approval_status_id: "",
    date_from: "",
    date_to: "",
};

// Skeleton columns for loading state
const skeletonColumns = [
    { name: "No.", skeletonWidth: "w-12" },
    { name: "No. Retur", skeletonWidth: "w-32" },
    { name: "No. Order", skeletonWidth: "w-32" },
    { name: "Supplier", skeletonWidth: "w-40" },
    { name: "Tanggal", skeletonWidth: "w-28" },
    { name: "Total", skeletonWidth: "w-32", align: "end" },
    { name: "Status", skeletonWidth: "w-24" },
    { name: "Aksi", skeletonWidth: "w-20", align: "end" },
];

const PurchaseOrderReturnData = () => {
    const { hasPermission } = useUser();
    const [approvalModal, setApprovalModal] = useState({
        isOpen: false,
        item: null,
    });

    const table = useDataTable(
        "purchase-order-returns/data",
        "purchase-order-returns/{id}/delete",
        {},
        initialFilters,
    );

    // Fetch filter options for label lookups
    const { data: approvalStatuses } = useFetchData("select/approval-status");

    // Filter options to pass to FilterData component
    const filterOptions = {
        approvalStatuses: approvalStatuses || [],
    };

    // Filter labels with dynamic getValueLabel
    const filterLabels = {
        return_number: { label: "No. Retur" },
        approval_status_id: {
            label: "Status Approval",
            getValueLabel: (val) => {
                const item = approvalStatuses.find(
                    (s) => String(s.value) === String(val),
                );
                return item?.title || val;
            },
        },
        date_from: { label: "Dari Tanggal" },
        date_to: { label: "Sampai Tanggal" },
    };

    // Handle remove single filter
    const handleRemoveFilter = (key) => {
        table.filterPanel.setPending((prev) => ({
            ...prev,
            [key]: initialFilters[key],
        }));
        table.filters.setFilter(key, initialFilters[key]);
    };

    // Derived helpers
    const isPending = (statusId) => Number(statusId) === 1;

    if (table.error) {
        return (
            <div className="p-6">
                <ErrorState
                    title="Gagal Memuat Data Retur Pembelian"
                    message={table.error?.message}
                    onRetry={table.refetch}
                />
            </div>
        );
    }

    return (
        <div className="flex h-full w-full flex-col gap-4">
            {/* Page Header */}
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <PageHeader
                    title="Retur Pembelian"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Pembelian" },
                        { label: "Retur Pembelian" },
                    ]}
                />
                <div className="flex items-center gap-2">
                    <FilterButton
                        isOpen={table.filterPanel.isOpen}
                        onToggle={() =>
                            table.filterPanel.setIsOpen(
                                !table.filterPanel.isOpen,
                            )
                        }
                        activeCount={table.filters.activeCount}
                    />
                    {hasPermission("purchase-order-return.create") && (
                        <Button
                            as={Link}
                            to="/purchasing/return/create"
                            color="primary"
                            startContent={<LuPlus />}
                            size="sm"
                        >
                            Buat Retur
                        </Button>
                    )}
                </div>
            </div>

            {/* Filter Panel */}
            <FilterData
                table={table}
                initialFilters={initialFilters}
                filterOptions={filterOptions}
            />

            {/* Active Filter Chips */}
            <ActiveFilterChips
                filters={table.filters.values}
                filterLabels={filterLabels}
                onRemove={handleRemoveFilter}
            />

            {/* Data Table */}
            <Card className="border-default-200 border" shadow="none">
                <CardHeader>
                    <DataTableControlBar table={table} />
                </CardHeader>
                <CardBody className="p-0">
                    {table.isLoading ? (
                        <DataTableSkeleton
                            columns={skeletonColumns}
                            rowCount={table.pagination.perPage}
                        />
                    ) : table.items.length === 0 ? (
                        <EmptyState
                            title={
                                table.filters.activeCount > 0
                                    ? "Data Tidak Ditemukan"
                                    : "Data Kosong"
                            }
                            description={
                                table.filters.activeCount > 0
                                    ? "Tidak ada data retur yang cocok dengan filter."
                                    : "Belum ada data retur pembelian."
                            }
                        />
                    ) : (
                        <Table aria-label="Tabel Retur Pembelian" shadow="none">
                            <TableHeader>
                                <TableColumn key="no" width={50}>
                                    No.
                                </TableColumn>
                                <TableColumn key="return_number">
                                    No. Retur
                                </TableColumn>
                                <TableColumn key="order_info">
                                    Purchase Order
                                </TableColumn>
                                <TableColumn key="date">Tanggal</TableColumn>
                                <TableColumn key="total" align="end">
                                    Nilai Retur
                                </TableColumn>
                                <TableColumn key="status">
                                    Status Approval
                                </TableColumn>
                                <TableColumn align="end" key="actions">
                                    Aksi
                                </TableColumn>
                            </TableHeader>
                            <TableBody items={table.items}>
                                {(item) => (
                                    <TableRow key={item.id}>
                                        {/* No. */}
                                        <TableCell>
                                            <ThemedText
                                                size="sm"
                                                variant="secondary"
                                            >
                                                {table.items.indexOf(item) +
                                                    1 +
                                                    (table.pagination.page -
                                                        1) *
                                                        table.pagination
                                                            .perPage}
                                                .
                                            </ThemedText>
                                        </TableCell>
                                        {/* Return Number */}
                                        <TableCell>
                                            <ThemedText
                                                size="sm"
                                                className="font-medium"
                                            >
                                                {item.return_number}
                                            </ThemedText>
                                        </TableCell>
                                        {/* Purchase Order Info */}
                                        <TableCell>
                                            <div className="flex flex-col">
                                                <ThemedText size="sm">
                                                    {item.returnable
                                                        ?.order_number || "-"}
                                                </ThemedText>
                                                <ThemedText
                                                    size="xs"
                                                    variant="secondary"
                                                >
                                                    {item.returnable?.supplier
                                                        ?.name || "-"}
                                                </ThemedText>
                                            </div>
                                        </TableCell>
                                        {/* Date */}
                                        <TableCell>
                                            <ThemedText size="sm">
                                                {formatDate(item.return_date)}
                                            </ThemedText>
                                        </TableCell>
                                        {/* Total Amount */}
                                        <TableCell>
                                            <ThemedText
                                                size="sm"
                                                className="font-medium"
                                            >
                                                {formatCurrency(
                                                    item.return_amount,
                                                )}
                                            </ThemedText>
                                        </TableCell>
                                        {/* Status */}
                                        <TableCell>
                                            <Chip
                                                color={
                                                    item.approval_status
                                                        ?.badge_color ||
                                                    "default"
                                                }
                                                size="sm"
                                                className="font-medium"
                                                variant="flat"
                                            >
                                                {item.approval_status?.name ||
                                                    "Unknown"}
                                            </Chip>
                                        </TableCell>
                                        {/* Actions */}
                                        <TableCell>
                                            <div className="relative flex items-center justify-end gap-2">
                                                <Tooltip content="Detail Retur">
                                                    <Button
                                                        isIconOnly
                                                        size="sm"
                                                        variant="light"
                                                        as={Link}
                                                        to={`/purchasing/return/${item.id}/detail`}
                                                    >
                                                        <LuEye
                                                            size={14}
                                                            className="text-default-500"
                                                        />
                                                    </Button>
                                                </Tooltip>
                                                {isPending(item.approval_status_id) && hasPermission("purchase-order-return.approve") && (
                                                    <Tooltip content="Ubah Status Approval">
                                                        <Button
                                                            isIconOnly
                                                            size="sm"
                                                            variant="light"
                                                            onPress={() =>
                                                                setApprovalModal(
                                                                    {
                                                                        isOpen: true,
                                                                        item: item,
                                                                    },
                                                                )
                                                            }
                                                        >
                                                            <LuRefreshCw
                                                                size={14}
                                                                className="text-default-500"
                                                            />
                                                        </Button>
                                                    </Tooltip>
                                                )}

                                                {/* Only allow edit/delete if Pending */}
                                                {isPending(item.approval_status_id) ? (
                                                    <>
                                                        {hasPermission("purchase-order-return.edit") && (
                                                            <Tooltip content="Edit Retur">
                                                                <Button
                                                                    isIconOnly
                                                                    size="sm"
                                                                    variant="light"
                                                                    as={Link}
                                                                    to={`/purchasing/return/${item.id}/edit`}
                                                                >
                                                                    <LuPencil
                                                                        size={14}
                                                                        className="text-default-500"
                                                                    />
                                                                </Button>
                                                            </Tooltip>
                                                        )}
                                                        {hasPermission("purchase-order-return.delete") && (
                                                            <Tooltip content="Hapus Retur">
                                                                <Button
                                                                    isIconOnly
                                                                    size="sm"
                                                                    color="danger"
                                                                    variant="light"
                                                                    onPress={() =>
                                                                        table.deleteState.openDeleteModal(
                                                                            item.id,
                                                                        )
                                                                    }
                                                                >
                                                                    <LuTrash size={14} />
                                                                </Button>
                                                            </Tooltip>
                                                        )}
                                                    </>
                                                ) : (
                                                    hasPermission("purchase-order-return.edit") && (
                                                        <Tooltip content="Tidak bisa diedit karena sudah diproses">
                                                            <span>
                                                                <Button
                                                                    isIconOnly
                                                                    size="sm"
                                                                    variant="light"
                                                                    isDisabled
                                                                    className="opacity-30"
                                                                >
                                                                    <LuPencil size={14} />
                                                                </Button>
                                                            </span>
                                                        </Tooltip>
                                                    )
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardBody>
                <CardFooter>
                    <DataTablePagination table={table} />
                </CardFooter>
            </Card>

            <DeleteConfirmModal table={table} />

            {/* Change Status Modal reused as Approval Modal */}
            <ChangeStatusModal
                isOpen={approvalModal.isOpen}
                title="Status Approval Retur"
                onClose={() => setApprovalModal({ isOpen: false, item: null })}
                item={approvalModal.item}
                itemName={approvalModal.item?.return_number}
                statusEndpoint="select/approval-status"
                statusField="approval_status_id"
                endpoint="purchase-order-returns/{id}/change-approval-status"
                onSuccess={() => table.refetch()}
                statusIdsRequiringConfirmation={[2]}
                confirmationMessage="Menyetujui retur akan menyebabkan perpindahan stok (Barang Keluar) secara otomatis. Pastikan data sudah benar."
            />
        </div>
    );
};

export default PurchaseOrderReturnData;
